import React, {useMemo, useState} from 'react';
import {sx} from './sx'
import PrimaryButtonSingleVoyage
	from "@containers/singleVoyage/ui/buttonts/primaryButtonSingleVoyage/PrimaryButtonSingleVoyage";
import SecondaryButtonSingleVoyage
	from "@containers/singleVoyage/ui/buttonts/secondaryButtonSingleVoyage/SecondaryButtonSingleVoyage";
import {useTranslation} from 'react-i18next';
import ActionUiHandler from "@ui/template/actionUiHandler/ActionUiHandler";
import {useValidationValue} from "@hooks/validate/useValidationValue";
import {array, object} from 'yup';
import {numberMin1Schema, stringReqSchema} from "@helpers/validate/validateSchema";
import {getErrorProps} from "@helpers/error";
import InputCustom from '@ui/inputs/InputCustom';
import {SubTaskCreationSingleVoyage, TaskCreationSingleVoyage} from "@containers/singleVoyage/tasks/types";
import SelectTaskTemplateSingleVoyage
	from "@containers/singleVoyage/taskTemplate/select/selectTaskTemplate/SelectTaskTemplateSingleVoyage";
import MuiDatePicker from "@ui/datePicker/muiDatePicker/MuiDatePicker";
import {DATE_FORMAT_PLAN_COMPLETION_TIME_TASK_SINGLE_VOYAGE} from "@containers/singleVoyage/tasks/constants/dateFormat";
import moment from 'moment'
import {OnSaveTaskSingleVoyage} from "@containers/singleVoyage/tasks/table/types/events";
import SubtasksSingleVoyage from "@containers/singleVoyage/tasks/mutateTask/subtasks/SubtasksSingleVoyage";
import SwitchCustom from "@ui/switchCustom/SwitchCustom";
import {TaskWayPointSingleVoyage} from "@containers/singleVoyage/tasks/table/types";
import SelectMultipleTasksWaypointsByDataSingleVoyage
	from "@containers/singleVoyage/tasks/mutateTask/select/selectWaypoints/SelectMultipleTasksWaypointsByDataSingleVoyage";
import DividerCustom from "@ui/dividerCustom/DividerCustom";
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import DescriptionIcon from '@mui/icons-material/Description';
import PlaceIcon from '@mui/icons-material/Place';
import ScheduleIcon from '@mui/icons-material/Schedule';
import {
	useGetTaskConfigByIdSingleVoyage
} from "@containers/singleVoyage/taskTemplate/hooks/useGetTaskConfigByIdSingleVoyage";

interface IProps {
	initial?: Partial<TaskCreationSingleVoyage>
	voyageId: number
	closeModalFn?: () => void
	onSave: OnSaveTaskSingleVoyage
	waypoints: TaskWayPointSingleVoyage[]
	accountId: string | number
}

const MutateTaskSingleVoyage = ({
									initial,
									closeModalFn,
									voyageId,
									onSave,
									waypoints,
									accountId,
								}: IProps) => {

	const [taskConfigTemplateId, setTaskConfigTemplateId] = useState<string>(initial?.taskConfigTemplateId?.toString() ?? '')
	const [title, setTitle] = useState<string>(initial?.title ?? '')
	const [description, setDescription] = useState<string>(initial?.description ?? '')
	const [hasPlanCompletionTime, setHasPlanCompletionTime] = useState<boolean>(!!initial?.planCompletionTime)
	const [planCompletionTime, setPlanCompletionTime] = useState<string>(moment(initial?.planCompletionTime ?? new Date()).format(DATE_FORMAT_PLAN_COMPLETION_TIME_TASK_SINGLE_VOYAGE))
	const [waypointIds, setWaypointIds] = useState<string[]>(initial?.waypointIds?.length >= 1 ? initial.waypointIds.map((id) => id.toString()) : [])
	const [subtasks, setSubtasks] = useState<SubTaskCreationSingleVoyage[]>(initial?.subtasks ?? [])

	const {t} = useTranslation()

	const {getTaskConfigById} = useGetTaskConfigByIdSingleVoyage({accountId})
	const config = getTaskConfigById(Number(taskConfigTemplateId))?.config
	const singleWaypointOnly = !subtasks.length && config?.waypointLimit === 'SINGLE'

	// A template with the SINGLE limit allows exactly one stop - the backend now
	// enforces it, so do not let the dispatcher walk into a save error.
	const hints = useMemo(() => {
		if (!config) {
			return []
		}
		return [
			singleWaypointOnly ? t('singleVoyage.singleWaypointLimit') : t('singleVoyage.noLimit'),
			...(config.photoRequired === 'YES' ? [t('singleVoyage.photoReport')] : []),
		]
	}, [config, singleWaypointOnly, t])

	const [validateFn, error, errorMessage] = useValidationValue({
		value: {
			title, description, waypointIds, taskConfigTemplateId
		},
		validateSchema: object({
			title: stringReqSchema,
			description: stringReqSchema,
			taskConfigTemplateId: stringReqSchema,
			waypointIds: array().of(stringReqSchema).min(1, t('minLengthElem1')).max(singleWaypointOnly ? 1 : Infinity, t('singleVoyage.singleWaypointLimit')),
		}),
		dependency: [title, description, waypointIds, taskConfigTemplateId, singleWaypointOnly]
	})


	const [validateFnSubtasks, errorSubtasks, errorMessageSubtasks] = useValidationValue({
		value: subtasks,
		validateSchema: array().of(object({
			title: stringReqSchema,
			description: stringReqSchema,
			taskConfigTemplateId: numberMin1Schema,
			waypointIds: array().of(numberMin1Schema.oneOf(waypointIds.map(Number))).min(1, t('minLengthElem1')),
		})),
		dependency: [subtasks, waypointIds]
	})

	const onSaveFn = async () => {
		const validate = validateFn()
		const validateSubtasks = validateFnSubtasks()
		if (!validate || !validateSubtasks) {
			return
		}

		await onSave({
			task: {
				accountId: Number(accountId),
				title,
				description,
				driverId: initial?.driverId || 0,
				waypointIds: waypointIds.map((id) => Number(id)),
				subtasks,
				planCompletionTime: hasPlanCompletionTime ? moment(planCompletionTime, DATE_FORMAT_PLAN_COMPLETION_TIME_TASK_SINGLE_VOYAGE).toISOString() : null,
				id: initial?.id,
				status: initial?.status || 'PLANNED',
				type: 'REGULAR',
				taskConfigTemplateId: Number(taskConfigTemplateId),
			},
			closeModalFn,
			voyageId,
		})

	}

	// Preserve child tasks when the parent scope changes; validate before saving.
	const handleChangeWaypoints = (ids: string[]) => {
		setWaypointIds(singleWaypointOnly ? ids.slice(-1) : ids)
	}

	return (
		<ActionUiHandler
			sx={sx.root}
		>
			<Box sx={sx.section}>
				<DividerCustom
					sx={sx.divider}
					label={t('base.description')}
					icon={<DescriptionIcon fontSize={'small'}/>}
				/>

				<InputCustom
					value={title}
					onChange={setTitle}
					label={t('base.name')}
					{...getErrorProps({errorMessage, error, key: 'title'})}
				/>

				<InputCustom
					value={description}
					onChange={setDescription}
					label={t('base.description')}
					{...getErrorProps({errorMessage, error, key: 'description'})}
					multiline
					rows={3}
				/>

				<SelectTaskTemplateSingleVoyage
					accountId={accountId}
					value={taskConfigTemplateId}
					onChange={setTaskConfigTemplateId}
					{...getErrorProps({errorMessage, error, key: 'taskConfigTemplateId'})}
				/>

				{hints.length >= 1 && <Box sx={sx.hints}>
					{hints.map((hint) => (
						<Typography key={hint} sx={sx.hint}>{hint}</Typography>
					))}
				</Box>}
			</Box>

			<Box sx={sx.section}>
				<DividerCustom
					sx={sx.divider}
					label={t('singleVoyage.executions')}
					icon={<PlaceIcon fontSize={'small'}/>}
				/>

				<SelectMultipleTasksWaypointsByDataSingleVoyage
					value={waypointIds}
					onChange={handleChangeWaypoints}
					waypoints={waypoints}
					hasSelectAll={!singleWaypointOnly}
					{...getErrorProps({errorMessage, error, key: 'waypointIds'})}
				/>
			</Box>

			<Box sx={sx.section}>
				<DividerCustom
					sx={sx.divider}
					label={t('base.planCompletionTime')}
					icon={<ScheduleIcon fontSize={'small'}/>}
				/>

				<SwitchCustom
					checked={hasPlanCompletionTime}
					onChange={setHasPlanCompletionTime}
					label={t('base.hasPlanCompletionTime')}
				/>

				{hasPlanCompletionTime && <MuiDatePicker
                    value={planCompletionTime}
                    onChange={setPlanCompletionTime}
                    format={DATE_FORMAT_PLAN_COMPLETION_TIME_TASK_SINGLE_VOYAGE}
                    showHoursAndMinutes
                    fullWidth={false}
                    sx={{padding: '0'}}
                    label={t('base.planCompletionTime')}
                />}
			</Box>

			{waypointIds?.length >= 1 && <SubtasksSingleVoyage
                subtasks={subtasks}
                setSubtasks={setSubtasks}
                waypoints={waypoints}
                error={errorSubtasks}
                errorMessage={errorMessageSubtasks}
                parentWaypointIds={waypointIds}
                accountId={accountId}
            />}

			<Box sx={sx.footer}>
				{closeModalFn && <SecondaryButtonSingleVoyage
                    sx={sx.btn}
                    onClick={closeModalFn}
                >
					{t('base.cancel')}
                </SecondaryButtonSingleVoyage>}

				<PrimaryButtonSingleVoyage
					sx={sx.btn}
					onClick={onSaveFn}
				>
					{t('base.save')}
				</PrimaryButtonSingleVoyage>
			</Box>

		</ActionUiHandler>
	);
};

export default MutateTaskSingleVoyage;
